import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton
} from '@mui/material';
import { ArrowBack, Send, Refresh, Map as MapIcon } from '@mui/icons-material';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import * as trackerApi from '../services/trackerApi';

const MT05S_COMMANDS = [
  { label: 'Check Status (111111CHK)', value: '111111CHK' },
  { label: 'Get Address (111111ADD)', value: '111111ADD' },
  { label: 'Get Google Map (111111MAP)', value: '111111MAP' },
  { label: 'Arm System (111111ARM)', value: '111111ARM' },
  { label: 'Disarm System (111111DIS)', value: '111111DIS' },
  { label: 'Stop Vehicle (111111STP)', value: '111111STP' },
  { label: 'Restore Vehicle (111111RES)', value: '111111RES' },
  { label: 'Speed Alert 80km/h (111111SPD080)', value: '111111SPD080' },
  { label: 'Speed Alert 100km/h (111111SPD100)', value: '111111SPD100' },
  { label: 'Geo-Info 30s x5 (t030s005n+111111)', value: 't030s005n+111111' },
  { label: 'Change Password (111111PSW)', value: '111111PSW' },
  { label: 'Custom Command', value: 'CUSTOM' }
];

const mapContainerStyle = {
  width: '100%',
  height: '400px'
};

const TrackerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tracker, setTracker] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedCommand, setSelectedCommand] = useState('');
  const [customCommand, setCustomCommand] = useState('');

  // Map state
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [markerPosition, setMarkerPosition] = useState(null);

  useEffect(() => {
    fetchTrackerData();
  }, [id]);

  const fetchTrackerData = async () => {
    try {
      setLoading(true);
      const [trackerData, logsData] = await Promise.all([
        trackerApi.getTrackerById(id),
        trackerApi.getTrackerLogs(id)
      ]);

      setTracker(trackerData.tracker);
      setLogs(logsData.logs || []);

      // Don't automatically show map on page load
      // Map will only be shown when user clicks on a log entry

      setError('');
    } catch (err) {
      setError(err.message || 'Failed to fetch tracker data');
      console.error('Error fetching tracker data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCommandChange = (e) => {
    setSelectedCommand(e.target.value);
    if (e.target.value !== 'CUSTOM') {
      setCustomCommand('');
    }
  };

  const handleSendCommand = async () => {
    try {
      const command = selectedCommand === 'CUSTOM' ? customCommand : selectedCommand;

      if (!command) {
        setError('Please select or enter a command');
        return;
      }

      setSending(true);
      const response = await trackerApi.sendCommand(id, command);
      setSuccess(`Command sent successfully! Refreshing in 10 seconds... ${response.twilioMessageId || ''}`);
      setError('');

      // Reset form
      setSelectedCommand('');
      setCustomCommand('');

      // Refresh logs after 10 seconds to allow tracker to respond
      setTimeout(() => {
        fetchTrackerData();
        setSuccess('Page refreshed with latest data');
      }, 10000);
    } catch (err) {
      setError(err.message || 'Failed to send command');
      console.error('Error sending command:', err);
    } finally {
      setSending(false);
    }
  };

  const handleLogClick = (log) => {
    if (log.latitude && log.longitude) {
      const position = {
        lat: parseFloat(log.latitude),
        lng: parseFloat(log.longitude)
      };
      setMapCenter(position);
      setMarkerPosition(position);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!tracker) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Tracker not found</Alert>
        <Button onClick={() => navigate('/dashboard/trackers')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard/trackers')}
          sx={{ mr: { xs: 0, sm: 2 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          {tracker.name}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Tracker Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Tracker Information
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">SIM Number</Typography>
              <Typography variant="body1" gutterBottom>{tracker.sim_number}</Typography>

              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Linked Bookmark</Typography>
              <Typography variant="body1" gutterBottom>
                {tracker.customId || 'Not linked'}
              </Typography>

              {tracker.bookmark && (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>License Plate</Typography>
                  <Typography variant="body1" gutterBottom>
                    {tracker.bookmark.vlicensePlate || '-'}
                  </Typography>

                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>VIN</Typography>
                  <Typography variant="body1" gutterBottom>
                    {tracker.bookmark.vvinNo || '-'}
                  </Typography>
                </>
              )}

              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Registered</Typography>
              <Typography variant="body1">
                {new Date(tracker.createdAt).toLocaleString()}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Send Commands */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Send MT05S Command
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Command</InputLabel>
                <Select
                  value={selectedCommand}
                  onChange={handleCommandChange}
                  label="Select Command"
                >
                  {MT05S_COMMANDS.map((cmd) => (
                    <MenuItem key={cmd.value} value={cmd.value}>
                      {cmd.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedCommand === 'CUSTOM' && (
                <TextField
                  label="Custom Command"
                  value={customCommand}
                  onChange={(e) => setCustomCommand(e.target.value)}
                  fullWidth
                  placeholder="Enter custom MT05S command"
                  helperText="Example: 111111PSW123456 (to change password to 123456)"
                />
              )}

              <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={sending ? <CircularProgress size={20} /> : <Send />}
                  onClick={handleSendCommand}
                  disabled={sending || (!selectedCommand || (selectedCommand === 'CUSTOM' && !customCommand))}
                  sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {sending ? 'Sending...' : 'Send Command'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchTrackerData}
                  sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Google Map */}
        {markerPosition && (
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Tracker Location
              </Typography>
              <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={15}
                >
                  {markerPosition && (
                    <Marker
                      position={markerPosition}
                      title={tracker.name}
                    />
                  )}
                </GoogleMap>
              </LoadScript>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Latitude: {markerPosition.lat.toFixed(6)}, Longitude: {markerPosition.lng.toFixed(6)}
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* Command Logs */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Command History ({logs.length})
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1976d2' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Timestamp</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Command</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Response</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Coordinates</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Status</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        No command history yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow
                        key={log.id}
                        hover={!!log.latitude}
                      >
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {new Date(log.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{log.command}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {log.response ? (
                            <Typography variant="body2" sx={{ maxWidth: { xs: 150, sm: 300 }, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {log.response.substring(0, 100)}{log.response.length > 100 ? '...' : ''}
                            </Typography>
                          ) : (
                            <Chip label="Pending" size="small" color="warning" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {log.latitude && log.longitude ? (
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {parseFloat(log.latitude).toFixed(4)}, {parseFloat(log.longitude).toFixed(4)}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {log.response ? (
                            <Chip label="Received" size="small" color="success" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                          ) : (
                            <Chip label="Sent" size="small" color="info" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {log.latitude && log.longitude ? (
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleLogClick(log)}
                              title="View on Map"
                            >
                              <MapIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                            </IconButton>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TrackerDetails;
